import React from "react";
import {
  DoorOpen,
  ExternalLink,
  Pencil,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import API_BASE from "../../api";

export const GROUP_WATER_TYPE_OPTIONS = [
  { value: "all", defaultLabel: "All" },
  { value: "lake", defaultLabel: "Lake" },
  { value: "river", defaultLabel: "River" },
  { value: "freshwater_any", defaultLabel: "Freshwater any" },
  { value: "saltwater_marine", defaultLabel: "Salt water (marine)" },
];

export const GROUP_SCOPE_FILTER_OPTIONS = [
  { value: "all", labelKey: "groups_scope_all", defaultLabel: "All groups" },
  { value: "mine", labelKey: "groups_scope_mine", defaultLabel: "My groups" },
  {
    value: "suggested",
    labelKey: "groups_scope_suggested",
    defaultLabel: "Suggested groups",
  },
];

export const GROUP_POSTING_APPROVAL_MODE_OPTIONS = [
  {
    value: "member_posts_auto_publish",
    defaultLabel: "Member posts publish immediately",
  },
  {
    value: "member_posts_need_approval",
    defaultLabel: "Member posts need admin approval",
  },
];

const GROUP_TYPE_OF_FISHING_LABEL_KEYS = {
  Spinning: "methods.spinning",
  Jigging: "methods.jigging",
  Popping: "methods.popping",
  "Fly Fishing": "methods.fly_fishing",
  "Bottom Fishing": "methods.bottom_fishing",
  "Bottom Fishing (casting)": "methods.bottom_fishing_casting",
  "Surf Casting": "methods.surf_casting",
  "Float Fishing": "methods.float_fishing",
  "Live Bait Fishing": "methods.live_bait",
  "Dead Bait Fishing": "methods.dead_bait",
  "Feeder Fishing": "methods.feeder",
  "Boat Spinning": "methods.boat_spinning",
  "Trolling (Towing)": "methods.trolling_towing",
  "Drift Fishing": "methods.drift_fishing",
  "Kayak Fishing": "methods.kayak_fishing",
  Other: "methods.other",
};

export function normalizeGroupMetadataValue(value) {
  if (typeof value !== "string") return "";
  const normalized = value.trim();
  if (!normalized || normalized.toLowerCase() === "all") {
    return "";
  }
  return normalized;
}

export function getEditableGroupMetadataValue(value) {
  return normalizeGroupMetadataValue(value) || "all";
}

export function isCurrentUserInGroup(group, userId) {
  if (!group || !userId) return false;
  return Boolean(group.members?.includes(userId) || group.admins?.includes(userId));
}

export function getGroupWaterTypeLabel(t, waterType) {
  const normalizedWaterType = normalizeGroupMetadataValue(waterType);
  if (!normalizedWaterType) return "";

  const option = GROUP_WATER_TYPE_OPTIONS.find((item) => item.value === normalizedWaterType);
  if (!option) return normalizedWaterType;

  return t(`groups_water_type_${option.value}`, {
    defaultValue: option.defaultLabel,
  });
}

export function getGroupWayOfFishingLabel(t, wayOfFishing) {
  const normalizedWayOfFishing = normalizeGroupMetadataValue(wayOfFishing);
  if (!normalizedWayOfFishing) return "";

  if (normalizedWayOfFishing === "Shore") {
    return t("fishing_way_shore", { defaultValue: normalizedWayOfFishing });
  }

  if (normalizedWayOfFishing === "Boat") {
    return t("fishing_way_boat", { defaultValue: normalizedWayOfFishing });
  }

  return normalizedWayOfFishing;
}

export function getGroupTypeOfFishingLabel(t, typeOfFishing) {
  const normalizedTypeOfFishing = normalizeGroupMetadataValue(typeOfFishing);
  if (!normalizedTypeOfFishing) return "";

  const translationKey = GROUP_TYPE_OF_FISHING_LABEL_KEYS[normalizedTypeOfFishing];
  if (!translationKey) {
    return normalizedTypeOfFishing;
  }

  return t(translationKey, { defaultValue: normalizedTypeOfFishing });
}

export function getGroupMetadataItems(group, t) {
  if (!group) return [];

  const items = [];
  const waterTypeLabel = getGroupWaterTypeLabel(t, group.waterType);
  const wayOfFishingLabel = getGroupWayOfFishingLabel(t, group.wayOfFishing);
  const typeOfFishingLabel = getGroupTypeOfFishingLabel(t, group.typeOfFishing);

  if (waterTypeLabel) {
    items.push({
      key: "waterType",
      label: t("groups_water_type", { defaultValue: "Water type" }),
      value: waterTypeLabel,
    });
  }

  if (wayOfFishingLabel) {
    items.push({
      key: "wayOfFishing",
      label: t("catch_way_of_fishing", { defaultValue: "Way of Fishing" }),
      value: wayOfFishingLabel,
    });
  }

  if (typeOfFishingLabel) {
    items.push({
      key: "typeOfFishing",
      label: t("catch_type_of_fishing", { defaultValue: "Type of Fishing" }),
      value: typeOfFishingLabel,
    });
  }

  return items;
}

export function formatGroupDate(timestamp) {
  if (!timestamp) return "";

  try {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function formatGroupPostDate(timestamp) {
  if (!timestamp) return "";

  try {
    return new Date(timestamp).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export function readErrorDetail(data, fallback) {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  return data.detail || fallback;
}

export function getGroupLocationSuggestionLabel(option) {
  if (typeof option === "string") return option;
  return option?.label || option?.name || "";
}

export function getGroupLocationSuggestionCoordinates(option) {
  if (!option || typeof option === "string") {
    return null;
  }

  const lat = Number(option.lat);
  const lng = Number(option.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return { lat, lng };
}

export function resolveGroupCoverUrl(coverImage) {
  if (!coverImage) return null;
  if (/^https?:\/\//i.test(coverImage)) return coverImage;
  return `${API_BASE}${coverImage}`;
}

export function resolveGroupPostMediaUrl(mediaUrl) {
  if (!mediaUrl) return null;
  if (/^https?:\/\//i.test(mediaUrl)) return mediaUrl;
  return `${API_BASE}${mediaUrl}`;
}

export function normalizeGroupCoverPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

export function resolveGroupCoverObjectPosition(group) {
  const positionX = normalizeGroupCoverPosition(group?.coverPositionX);
  const positionY = normalizeGroupCoverPosition(group?.coverPositionY);
  return `${100 - positionX}% ${100 - positionY}%`;
}

export function getGroupCoverScale(group) {
  const scale = group?.coverScale;
  if (!scale || !Number.isFinite(scale)) return 1;
  return Math.max(1, Math.min(3, scale));
}

export function getGroupLocationText(group) {
  if (group?.groupType !== "local") return "";
  return group.locationName || "";
}

export function GroupInvitePanel({ group, controller, t }) {
  if (!group || controller.inviteGroupId !== group.id || !controller.isAdmin(group)) {
    return null;
  }

  const eligibleFriends = controller.getEligibleFriendUsers(group);
  const pendingFriends = controller.getPendingFriendUsers(group);

  return (
    <div
      ref={controller.inviteGroupId === controller.activeGroupHomeId ? controller.groupInvitePanelRef : null}
      className="groups-invite-panel"
    >
      <h4>{t("groups_invite_friends")}</h4>
      {eligibleFriends.length > 0 ? (
        <>
          <p className="groups-invite-subtitle">
            {t("groups_invite_subtitle")}
          </p>
          <div className="groups-invite-list">
            {eligibleFriends.map((friend) => (
              <label key={friend.id} className="groups-invite-option">
                <input
                  type="checkbox"
                  checked={controller.selectedInviteIds.includes(friend.id)}
                  onChange={() => controller.toggleInviteSelection(friend.id)}
                />
                <span>{friend.username}</span>
              </label>
            ))}
          </div>
        </>
      ) : (
        <p className="groups-invite-subtitle">
          {t("groups_invite_none_available")}
        </p>
      )}

      {pendingFriends.length > 0 && (
        <p className="groups-invite-pending">
          {t("groups_invite_pending")}{" "}
          {pendingFriends.map((friend) => friend.username).join(", ")}
        </p>
      )}

      {controller.inviteError ? <p className="groups-action-error">{controller.inviteError}</p> : null}

      <div className="groups-invite-actions">
        <button
          type="button"
          className="groups-secondary-btn"
          onClick={() => controller.resetInviteState(null)}
          disabled={controller.inviteLoading}
        >
          {t("cancel")}
        </button>
        <button
          type="button"
          className="groups-primary-btn"
          onClick={() => controller.handleInviteFriends(group)}
          disabled={
            controller.inviteLoading ||
            eligibleFriends.length === 0 ||
            controller.selectedInviteIds.length === 0
          }
        >
          {controller.inviteLoading
            ? t("groups_inviting")
            : t("groups_send_invites")}
        </button>
      </div>
    </div>
  );
}

export function GroupActionButtons({
  group,
  controller,
  t,
  detailMode = false,
  options = {},
}) {
  if (!group) return null;

  const groupMember = controller.isMember(group);
  const groupAdmin = controller.isAdmin(group);
  const groupPendingJoinRequest = controller.hasPendingJoinRequest(group);
  const groupInvite = controller.hasGroupInvite(group);
  const eligibleFriends = controller.getEligibleFriendUsers(group);
  const pendingFriends = controller.getPendingFriendUsers(group);
  const groupActionLoading = Boolean(controller.actionLoadingById[group.id]);
  const canInvite =
    groupAdmin && (eligibleFriends.length > 0 || pendingFriends.length > 0);
  const showOpenGroup = groupMember && !options.hideOpenGroup;
  const leaveBlockedByLastMember = groupMember && group.members.length <= 1;
  const lastMemberTooltipId = `groups-last-member-tooltip-${group.id}`;

  return (
    <>
      {!detailMode && (
        <button
          type="button"
          className="groups-action-btn groups-view-btn"
          onClick={() => controller.openGroup(group.id)}
        >
          {t("groups_view_group")}
        </button>
      )}

      {showOpenGroup && (
        <button
          type="button"
          className="groups-action-btn groups-open-btn"
          onClick={() => controller.openGroupHome(group.id)}
          disabled={groupActionLoading}
        >
          <ExternalLink size={16} strokeWidth={2.3} aria-hidden="true" />
          {t("groups_open_group")}
        </button>
      )}

      {groupMember ? (
        leaveBlockedByLastMember ? (
          <div
            className="groups-action-tooltip-wrap groups-leave-action-wrap"
            tabIndex={0}
            aria-describedby={lastMemberTooltipId}
          >
            <button
              type="button"
              className="groups-action-btn groups-leave-btn"
              disabled
            >
              <DoorOpen size={16} strokeWidth={2.3} aria-hidden="true" />
              {t("groups_leave_group")}
            </button>
            <div
              id={lastMemberTooltipId}
              role="tooltip"
              className="groups-action-tooltip"
            >
              {t("groups_last_member_hint")}
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="groups-action-btn groups-leave-btn"
            onClick={() => controller.openConfirmModal("leave", group)}
            disabled={groupActionLoading}
          >
            <DoorOpen size={16} strokeWidth={2.3} aria-hidden="true" />
            {groupActionLoading
              ? t("groups_updating")
              : t("groups_leave_group")}
          </button>
        )
      ) : groupPendingJoinRequest ? (
        <button
          type="button"
          className="groups-action-btn groups-join-btn"
          disabled
        >
          {t("groups_pending_approval")}
        </button>
      ) : (
        <button
          type="button"
          className="groups-action-btn groups-join-btn"
          onClick={() => controller.handleMembershipAction(group, "join")}
          disabled={groupActionLoading}
        >
          <Users size={16} strokeWidth={2.3} aria-hidden="true" />
          {groupActionLoading
            ? t("groups_joining")
            : group.visibility === "private" && !groupInvite
              ? t("groups_request_to_join")
              : t("groups_join_group")}
        </button>
      )}

      {groupAdmin && (
        <button
          type="button"
          className="groups-action-btn groups-invite-btn"
          onClick={() => {
            if (detailMode) {
              controller.resetInviteState(group.id);
            } else {
              controller.openGroup(group.id, { showInvite: true });
            }
          }}
          disabled={!canInvite && !detailMode}
        >
          <UserPlus size={16} strokeWidth={2.3} aria-hidden="true" />
          {t("groups_invite_friends")}
        </button>
      )}

      {groupAdmin && detailMode && (
        <button
          type="button"
          className="groups-action-btn groups-view-btn groups-edit-btn"
          onClick={() => controller.openGroupMetadataEditor(group)}
          disabled={controller.groupMetadataEditLoading}
        >
          <Pencil size={16} strokeWidth={2.3} aria-hidden="true" />
          {controller.groupMetadataEditLoading &&
          controller.groupMetadataEditTargetId === group.id
            ? t("groups_updating")
            : t("groups_edit_metadata", { defaultValue: "Edit group details" })}
        </button>
      )}

      {groupAdmin && detailMode && (
        <button
          type="button"
          className="groups-action-btn groups-delete-btn"
          onClick={() => controller.openConfirmModal("delete", group)}
          disabled={groupActionLoading}
        >
          <Trash2 size={16} strokeWidth={2.3} aria-hidden="true" />
          {groupActionLoading
            ? t("groups_updating")
            : t("groups_delete_group")}
        </button>
      )}

      {groupMember && !groupAdmin && detailMode && (
        <p className="groups-action-hint">
          {t("groups_member_role_hint")}
        </p>
      )}

      {groupMember && groupAdmin && detailMode && (
        <p className="groups-action-hint">
          {t("groups_admin_role_hint")}
        </p>
      )}

      {controller.actionErrorById[group.id] ? (
        <p className="groups-action-error">{controller.actionErrorById[group.id]}</p>
      ) : null}
    </>
  );
}

function GroupPeopleStat({ group, type, controller, t }) {
  const isAdmins = type === "admins";

  return (
    <button
      type="button"
      className="groups-detail-stat groups-detail-stat-button"
      onClick={() => controller.openPeopleList(group.id, type)}
    >
      <span>
        {isAdmins
          ? t("groups_admins_label")
          : t("groups_members_label")}
      </span>
      <strong>{isAdmins ? group.admins.length : group.members.length}</strong>
    </button>
  );
}

export function GroupStatusBadges({ group, t, compact = false }) {
  if (!group) return null;

  const visibilityClassName = compact
    ? "groups-visibility-pill groups-visibility-pill-compact"
    : "groups-visibility-pill";

  return (
    <>
      <span className={visibilityClassName}>
        {group.visibility === "private"
          ? t("groups_private")
          : t("groups_public")}
      </span>
    </>
  );
}

function GroupMetadataStats({ group, t }) {
  return getGroupMetadataItems(group, t).map((item) => (
    <div
      key={`${group.id}-${item.key}`}
      className={[
        "groups-detail-stat",
        "groups-detail-stat--metadata",
        `groups-detail-stat--${item.key}`,
      ].join(" ")}
    >
      <span>{item.label}</span>
      <strong>{item.value}</strong>
    </div>
  ));
}

export function GroupOverviewStats({ group, controller, t }) {
  if (!group) return null;

  const locationText = getGroupLocationText(group);

  return (
    <>
      <GroupPeopleStat group={group} type="members" controller={controller} t={t} />
      <GroupPeopleStat group={group} type="admins" controller={controller} t={t} />
      <div className="groups-detail-stat">
        <span>{t("groups_created")}</span>
        <strong>{formatGroupDate(group.createdAt)}</strong>
      </div>
      {locationText ? (
        <div className="groups-detail-stat groups-detail-stat-wide">
          <span>{t("groups_location", { defaultValue: "Location" })}</span>
          <strong>{locationText}</strong>
        </div>
      ) : null}
      <GroupMetadataStats group={group} t={t} />
    </>
  );
}

export function GroupMetadataPills({ group, t }) {
  if (!group) return null;

  const metadataItems = getGroupMetadataItems(group, t);
  if (metadataItems.length === 0) return null;

  return (
    <div className="groups-card-metadata">
      {metadataItems.map((item) => (
        <div key={`${group.id}-${item.key}`} className="groups-card-metadata-pill">
          <span className="groups-card-metadata-label">{item.label}</span>
          <strong className="groups-card-metadata-value">{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

export function GroupPostMedia({ post, displayName, t }) {
  if (!Array.isArray(post?.media) || post.media.length === 0) {
    return null;
  }

  return (
    <div className="groups-post-media-grid">
      {post.media.map((item, index) => {
        const mediaUrl = resolveGroupPostMediaUrl(item.url);
        if (!mediaUrl) {
          return null;
        }

        const isVideo =
          item.mediaType === "video" || /\.(mp4|mov|avi|mkv|webm)$/i.test(item.url || "");

        return isVideo ? (
          <video
            key={`${post.id}-media-${index}`}
            src={mediaUrl}
            controls
            className="groups-post-media-item groups-post-media-video"
          />
        ) : (
          <img
            key={`${post.id}-media-${index}`}
            src={mediaUrl}
            alt={t("groups_post_attachment_alt", {
              defaultValue: "{{displayName}} post attachment {{index}}",
              displayName,
              index: index + 1,
            })}
            className="groups-post-media-item groups-post-media-image"
          />
        );
      })}
    </div>
  );
}

export function GroupMemberList({
  group,
  controller,
  t,
  memberIds,
  showAdminActions = false,
}) {
  if (!group) return null;

  const resolvedMemberIds = memberIds || group.members;
  const canShowAdminActions = Boolean(showAdminActions && controller.isAdmin(group));

  return resolvedMemberIds.map((memberId) => {
    const member = controller.getUserById(memberId);
    const isCreator = memberId === group.createdBy;
    const memberIsAdmin = group.admins.includes(memberId);
    const displayName =
      member?.username ||
      t("groups_unknown_member");
    const avatarUrl = member?.avatar ? `${API_BASE}${member.avatar}` : null;
    const subtitle = isCreator
      ? t("groups_creator")
      : memberIsAdmin
        ? t("groups_admin")
        : t("groups_member");
    const canManageMember = canShowAdminActions && controller.user?.id !== memberId;
    const rowActionKeyPrefix = `${group.id}:${memberId}`;
    const isPromoteLoading =
      controller.groupMemberActionLoadingKey === `promote:${rowActionKeyPrefix}`;
    const isDemoteLoading =
      controller.groupMemberActionLoadingKey === `demote:${rowActionKeyPrefix}`;
    const isRemoveLoading =
      controller.groupMemberActionLoadingKey === `remove:${rowActionKeyPrefix}`;
    const memberProfileButton = (
      <button
        key={memberId}
        type="button"
        className="groups-member-item groups-member-item-button"
        onClick={() => controller.handleOpenMemberProfile(member)}
      >
        <div className="groups-member-avatar">
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} />
          ) : (
            <span>{displayName.charAt(0).toUpperCase()}</span>
          )}
        </div>
        <div className="groups-member-meta">
          <strong className="groups-member-name">{displayName}</strong>
          <span className="groups-member-subtitle">{subtitle}</span>
        </div>
        {memberIsAdmin && (
          <span className="groups-role-badge groups-role-admin">
            {t("groups_admin")}
          </span>
        )}
      </button>
    );

    if (!canManageMember) {
      return memberProfileButton;
    }

    return (
      <div key={memberId} className="groups-member-row">
        {memberProfileButton}
        <div className="groups-member-row-actions">
          {!memberIsAdmin ? (
            <button
              type="button"
              className="groups-member-action-btn groups-member-action-promote"
              onClick={() => controller.handleGroupMemberManagementAction(group, memberId, "promote")}
              disabled={Boolean(controller.groupMemberActionLoadingKey)}
            >
              {isPromoteLoading
                ? t("groups_updating")
                : t("groups_make_admin", { defaultValue: "Make admin" })}
            </button>
          ) : (
            <button
              type="button"
              className="groups-member-action-btn groups-member-action-demote"
              onClick={() => controller.openConfirmModal("demoteAdmin", group, memberId)}
              disabled={Boolean(controller.groupMemberActionLoadingKey)}
            >
              {isDemoteLoading
                ? t("groups_updating")
                : t("groups_remove_admin", { defaultValue: "Remove admin" })}
            </button>
          )}
          <button
            type="button"
            className="groups-member-action-btn groups-member-action-remove"
            onClick={() => controller.openConfirmModal("removeMember", group, memberId)}
            disabled={Boolean(controller.groupMemberActionLoadingKey)}
          >
            {isRemoveLoading
              ? t("groups_updating")
              : t("groups_remove_member", { defaultValue: "Remove from group" })}
          </button>
        </div>
      </div>
    );
  });
}
